import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImeiHubServiceId } from './ImeiHubSignalIcon'
import {
  buildImeiHubPreviewSections,
  interpolateHubPreviewDetail,
} from '@redevicer/shared'
import {
  authTokenForImeiHub,
  formatImeiHubCheckError,
  hubServicePaidInCatalog,
  loadHubCatalogState,
  redirectToImeiHubCheckout,
  type HubCatalogState,
} from '../utils/imeiHubCatalogClient'
import {
  buildImeiReportViewModel,
  type ImeiCheckPayload,
  type ReportSection,
} from '../utils/imeiReportModel'
import { imeiReportPdfDownloadFilename } from '@redevicer/shared'
import { downloadVerifyReportPdfFromElement } from '../utils/captureVerifyReportPdf'
import { checkImei } from '../api/client'
import { isLikelyImei15 } from '../utils/extractImei'
import { dispatchImeiFollowUpCheck } from '../utils/imeiFollowUpCheck'
import { notifyImeiReportsUpdated } from '../utils/imeiReportsUnread'
import { rememberImeiCheckDigits, resolveImeiForMaskedReport } from '../utils/imeiReportImeiCache'
import { mapToVerifyReportModel } from '../utils/verifyReportAdapter'
import VerifyPremiumReport from './verify-report/VerifyPremiumReport'
import { imeiDigitsMatchMasked, maskImeiDigits } from '../utils/imeiReportModel'

export type ImeiVerifyReportVariant = 'inline' | 'compact' | 'full'

type Props = {
  variant?: ImeiVerifyReportVariant
  /** Hub marketing card — show report UI without login gate or actions. */
  preview?: boolean
  service: ImeiHubServiceId
  data: ImeiCheckPayload
  imeiMasked: string
  checkedAt?: string | Date
  token?: string | null
  onRequireLogin?: () => void
  onNewCheck?: () => void
  onOpen?: () => void
  onClose?: () => void
  /** Full IMEI when available — enables follow-up checks from unverified rows. */
  imeiDigits?: string | null
  onFollowUpCheck?: (service: ImeiHubServiceId, checkId: string) => void
}

function formatWhen(value: string | Date | undefined, locale: string): string {
  if (!value) return ''
  try {
    const date = value instanceof Date ? value : new Date(value)
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  } catch {
    return String(value)
  }
}

export function ImeiVerifyReportSkeleton() {
  return (
    <div className="vr-report vr-report--inline vr-report--loading" aria-hidden="true">
      <div className="vr-skeleton vr-skeleton--hero" />
      <div className="vr-skeleton vr-skeleton--verdict" />
      <div className="vr-skeleton-grid">
        <div className="vr-skeleton vr-skeleton--row" />
        <div className="vr-skeleton vr-skeleton--row" />
        <div className="vr-skeleton vr-skeleton--row" />
        <div className="vr-skeleton vr-skeleton--row" />
      </div>
    </div>
  )
}

export default function ImeiVerifyReport({
  variant = 'inline',
  preview = false,
  service,
  data,
  imeiMasked,
  checkedAt,
  token = null,
  onRequireLogin,
  onNewCheck,
  onOpen,
  onClose,
  imeiDigits = null,
  onFollowUpCheck,
}: Props) {
  const { t, i18n } = useTranslation()
  const [pdfBusy, setPdfBusy] = useState(false)
  const [pdfError, setPdfError] = useState('')
  const [pdfCapture, setPdfCapture] = useState(false)
  const reportRef = useRef<HTMLElement>(null)
  const [followUpBusy, setFollowUpBusy] = useState(false)
  const [followUpError, setFollowUpError] = useState('')
  const [sessionImeiDigits, setSessionImeiDigits] = useState<string | null>(null)
  const [pendingFollowUp, setPendingFollowUp] = useState<{
    service: ImeiHubServiceId
    checkId: string
  } | null>(null)
  const [followUpImeiEntry, setFollowUpImeiEntry] = useState('')
  const [followUpImeiEntryError, setFollowUpImeiEntryError] = useState('')
  const [hubCatalog, setHubCatalog] = useState<HubCatalogState>({
    paidPriceCents: {},
    stripeCheckoutEnabled: false,
  })
  const [liveCheck, setLiveCheck] = useState<{
    service: ImeiHubServiceId
    data: ImeiCheckPayload
    checkedAt: Date
    imeiMasked: string
  } | null>(null)

  const activeService = liveCheck?.service ?? service
  const activeData = liveCheck?.data ?? data
  const activeImeiMasked = liveCheck?.imeiMasked ?? imeiMasked
  const activeCheckedAt = liveCheck?.checkedAt ?? checkedAt

  const vm = useMemo(
    () => buildImeiReportViewModel(activeData, activeService),
    [activeData, activeService],
  )
  const when = formatWhen(activeCheckedAt ?? new Date(), i18n.language)
  const serviceTitle = t(`vc.imeiHub.services.${activeService}.title`)
  const summary = t(vm.summaryKey, { defaultValue: vm.summaryFallback })

  const riskSections = vm.sections.filter((s) => s.tone === 'risk' || s.tone === 'warn')
  const infoSections = vm.sections.filter((s) => s.tone !== 'risk' && s.tone !== 'warn')
  const displaySections = useMemo((): ReportSection[] => {
    if (preview) {
      return buildImeiHubPreviewSections(activeService).map((row) => ({
        id: row.id,
        tone: row.tone,
        titleKey: row.titleKey,
        detail: interpolateHubPreviewDetail(t(row.detailKey), activeData),
      }))
    }
    return [...riskSections, ...infoSections]
  }, [preview, activeService, activeData, riskSections, infoSections, t])

  const guestLocked = !preview && !token && variant === 'inline'

  useEffect(() => {
    if (preview || guestLocked) return
    void loadHubCatalogState().then(setHubCatalog)
  }, [preview, guestLocked])

  const reportCreatedAtIso = useMemo(() => {
    if (!activeCheckedAt) return ''
    if (activeCheckedAt instanceof Date) return activeCheckedAt.toISOString()
    return String(activeCheckedAt)
  }, [activeCheckedAt])

  const cachedImeiForReport = useMemo(
    () => resolveImeiForMaskedReport(activeImeiMasked, reportCreatedAtIso),
    [activeImeiMasked, reportCreatedAtIso],
  )

  const effectiveImeiDigits = useMemo(() => {
    for (const raw of [sessionImeiDigits, imeiDigits, cachedImeiForReport]) {
      const digits = (raw ?? '').replace(/\D/g, '')
      if (
        digits.length === 15 &&
        isLikelyImei15(digits) &&
        imeiDigitsMatchMasked(digits, activeImeiMasked)
      ) {
        return digits
      }
    }
    return null
  }, [sessionImeiDigits, imeiDigits, cachedImeiForReport, activeImeiMasked])

  const report = useMemo(
    () =>
      mapToVerifyReportModel({
        vm,
        data: activeData,
        sections: displaySections,
        imeiMasked: activeImeiMasked,
        checkedAtLabel: when,
        serviceTitle,
        summaryText: summary,
        savedToProfile: Boolean(token),
        hubService: activeService,
        translateDetail: (detail) => (detail && detail.startsWith('vc.') ? t(detail) : detail),
        translateKey: (key) => t(key),
      }),
    [
      vm,
      activeData,
      displaySections,
      activeImeiMasked,
      when,
      serviceTitle,
      summary,
      token,
      activeService,
      t,
    ],
  )

  async function runFollowUpCheck(
    targetService: ImeiHubServiceId,
    digits: string,
    checkoutSessionId?: string | null,
  ) {
    setFollowUpBusy(true)
    setFollowUpError('')
    try {
      if (
        !checkoutSessionId &&
        hubServicePaidInCatalog(hubCatalog, targetService) &&
        hubCatalog.stripeCheckoutEnabled
      ) {
        const authToken = authTokenForImeiHub(token)
        await redirectToImeiHubCheckout(digits, targetService, authToken)
        return
      }

      const authToken = authTokenForImeiHub(token)
      const res = await checkImei(digits, authToken, targetService, checkoutSessionId)
      const result = res.data as ImeiCheckPayload
      const masked = maskImeiDigits(digits)
      rememberImeiCheckDigits(digits, masked)
      setSessionImeiDigits(digits)
      const checkedAtLive = new Date()
      setLiveCheck({
        service: targetService,
        data: result,
        checkedAt: checkedAtLive,
        imeiMasked: masked,
      })
      if (authToken) notifyImeiReportsUpdated()
    } catch (err) {
      setFollowUpError(formatImeiHubCheckError(err, t))
    } finally {
      setFollowUpBusy(false)
    }
  }

  async function handleFollowUpCheck(targetService: ImeiHubServiceId, checkId: string) {
    if (onFollowUpCheck) {
      onFollowUpCheck(targetService, checkId)
      return
    }

    if (!effectiveImeiDigits) {
      if (variant === 'full' || variant === 'inline') {
        const prefill =
          resolveImeiForMaskedReport(activeImeiMasked, reportCreatedAtIso) ??
          followUpImeiEntry.replace(/\D/g, '')
        const prefillDigits = prefill.replace(/\D/g, '')
        if (
          prefillDigits.length === 15 &&
          isLikelyImei15(prefillDigits) &&
          imeiDigitsMatchMasked(prefillDigits, activeImeiMasked)
        ) {
          setSessionImeiDigits(prefillDigits)
          await runFollowUpCheck(targetService, prefillDigits)
          return
        }
        setPendingFollowUp({ service: targetService, checkId })
        setFollowUpImeiEntry(prefillDigits.length === 15 ? prefillDigits : prefill)
        setFollowUpImeiEntryError('')
        setFollowUpError('')
        return
      }
      dispatchImeiFollowUpCheck({
        service: targetService,
        checkId,
        imei: imeiDigits ?? undefined,
      })
      return
    }

    setPendingFollowUp(null)
    await runFollowUpCheck(targetService, effectiveImeiDigits)
  }

  function handleFollowUpImeiSubmit(event?: FormEvent) {
    event?.preventDefault()
    const digits = followUpImeiEntry.replace(/\D/g, '')
    if (!isLikelyImei15(digits)) {
      setFollowUpImeiEntryError(t('vc.imeiHub.result.invalid'))
      return
    }
    if (!imeiDigitsMatchMasked(digits, activeImeiMasked)) {
      setFollowUpImeiEntryError(t('vc.imeiReport.followUp.mismatch', { masked: activeImeiMasked }))
      return
    }
    setFollowUpImeiEntryError('')
    const pending = pendingFollowUp
    setPendingFollowUp(null)
    if (pending) {
      void runFollowUpCheck(pending.service, digits)
    }
  }

  async function handleDownloadPdf() {
    if (pdfBusy || preview || guestLocked) return
    const root = reportRef.current
    if (!root) return
    setPdfBusy(true)
    setPdfError('')
    setPdfCapture(true)
    try {
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      })
      await downloadVerifyReportPdfFromElement(
        root,
        imeiReportPdfDownloadFilename(activeImeiMasked),
      )
    } catch {
      setPdfError(t('vc.imeiReport.downloadPdfFailed'))
    } finally {
      setPdfCapture(false)
      setPdfBusy(false)
    }
  }

  const showDownloadPdf = !preview && !guestLocked && vm.status !== 'balance'

  if (variant === 'compact') {
    return (
      <button type="button" className="vc-imei-report vc-imei-report--compact" onClick={onOpen}>
        <div className="vc-imei-report__compact-main">
          <span className="vc-imei-report__compact-device">
            {vm.deviceLabel || serviceTitle}
          </span>
          <span className="vc-imei-report__compact-imei">{imeiMasked}</span>
          {when ? <time className="vc-imei-report__compact-when">{when}</time> : null}
        </div>
        <span className={`vc-imei-report__status-pill vc-imei-report__status-pill--${vm.status}`}>
          {t(
            vm.status === 'clean'
              ? 'vc.imeiReport.status.clean'
              : vm.status === 'warning'
                ? 'vc.imeiReport.status.warning'
                : vm.status === 'high_risk'
                  ? 'vc.imeiReport.status.highRisk'
                  : vm.status === 'invalid'
                    ? 'vc.imeiReport.status.invalid'
                    : 'vc.imeiReport.status.balance',
          )}
        </span>
      </button>
    )
  }

  const premiumVariant = variant === 'full' ? 'full' : 'inline'

  return (
    <VerifyPremiumReport
      ref={reportRef}
      report={report}
      service={activeService}
      serviceTitle={serviceTitle}
      variant={premiumVariant}
      preview={preview}
      exportMode={pdfCapture}
      onClose={onClose}
      onDownloadPdf={() => void handleDownloadPdf()}
      pdfBusy={pdfBusy}
      pdfError={pdfError}
      onNewCheck={preview ? undefined : onNewCheck}
      onRequireLogin={preview ? undefined : onRequireLogin}
      showDownloadPdf={showDownloadPdf}
      guestLocked={guestLocked}
      followUpBusy={followUpBusy}
      followUpError={followUpError}
      followUpImeiPrompt={
        pendingFollowUp && !preview && !guestLocked
          ? {
              masked: activeImeiMasked,
              serviceTitle: t(`vc.imeiHub.services.${pendingFollowUp.service}.title`),
              value: followUpImeiEntry,
              error: followUpImeiEntryError,
              busy: followUpBusy,
              onChange: setFollowUpImeiEntry,
              onSubmit: handleFollowUpImeiSubmit,
              onCancel: () => {
                setPendingFollowUp(null)
                setFollowUpImeiEntry('')
                setFollowUpImeiEntryError('')
              },
            }
          : null
      }
      onFollowUpCheck={preview || guestLocked || followUpBusy ? undefined : handleFollowUpCheck}
      followUpPriceCents={hubCatalog.paidPriceCents}
    />
  )
}
